// ✅ src/pages/OrderManagement/BillGeneration.jsx
// FULL FILE - UPDATED WITH LOCALSTORAGE HELPER + COMMON HEADERS
//
// Backend mount:
//   app.use("/api/billgeneration", billgeneration);
//
// Endpoints used:
//   GET  http://nissi-india.com/api/billgeneration/projects?branchId=1
//   GET  http://nissi-india.com/api/billgeneration/list?branchId=1
//   GET  http://nissi-india.com/api/billgeneration/view/:billGenerationId
//   POST http://nissi-india.com/api/billgeneration/save

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../../components/ProjectPostOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSave,
  FaChevronUp,
  FaChevronDown,
  FaEye,
  FaUndo,
} from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/billgeneration";

const toNum = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/,/g, "").trim();
  if (s === "") return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toStr = (v) => String(v ?? "").trim();

const fmtMoney0 = (v) =>
  toNum(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

// ✅ NEW: dd/MM/yyyy -> yyyy-MM-dd (needed so the native <input type="date">
// calendar picker can display/accept the value we already store as dd/MM/yyyy)
const dmyToISO = (dmy) => {
  if (!dmy) return "";
  const m = String(dmy).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const normalizeDateIn = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return isoToDDMMYYYY(s);
  return s;
};

const dateForSave = (dmy) => (dmy ? String(dmy).trim() : "");

const emptyForm = {
  projectId: "",
  billNo: "",
  brNo: "",
  mbNo: "",
  billAmount: "",
  it: "",
  itPercent: "",
  st: "",
  stPercent: "",
  fsd: "",
  fsdPercent: "",
  cwc: "",
  cwcPercent: "",
  cbf: "",
  cbfPercent: "",
  penalty: "",
  paid: false,
  byCheque: "",
  byAdj: "",
  payDate: "",
  chqNo: "",
  dateOfRealisation: "",
  refundDate: "",
  remarks: "",
  otherRecoveries1: "",
  otherRecoveries2: "",
};

const BillGeneration = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ safe localStorage helper
  const getLS = (key, fallback = "") => {
    try {
      if (typeof window === "undefined") return fallback;
      const val = localStorage.getItem(key);
      return val !== null && val !== undefined ? val : fallback;
    } catch (err) {
      return fallback;
    }
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");

  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(() => toStr(branchTypeLS || "HO"), [branchTypeLS]);
  const loginUserId = useMemo(() => toStr(userIdLS), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [projects, setProjects] = useState([]);
  const [billRows, setBillRows] = useState([]);
  const [loadingGrid, setLoadingGrid] = useState(false);

  const [mode, setMode] = useState("N");
  const [billGenerationId, setBillGenerationId] = useState(0);
  const [lockProjectPick, setLockProjectPick] = useState(false);

  const [formData, setFormData] = useState(emptyForm);

  const isLockedByPaid = !!formData.paid;

  const setField = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };

  const resetToNew = () => {
    setMode("N");
    setBillGenerationId(0);
    setLockProjectPick(false);
    setFormData(emptyForm);
    setShowFilters(true);
  };

  const validate = () => {
    if (!formData.projectId) {
      toast.error("Please select Project Name / Project Code");
      return false;
    }
    if (!String(formData.billNo || "").trim()) {
      toast.error("Bill No is required");
      return false;
    }
    if (!String(formData.mbNo || "").trim()) {
      toast.error("M.B No is required");
      return false;
    }
    if (!String(formData.billAmount || "").trim()) {
      toast.error("Bill Amount is required");
      return false;
    }
    if (!String(formData.payDate || "").trim()) {
      toast.error("Pay Date is required (dd/MM/yyyy)");
      return false;
    }
    if (!String(formData.dateOfRealisation || "").trim()) {
      toast.error("Date Of Realisation is required (dd/MM/yyyy)");
      return false;
    }
    return true;
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_BASE}/projects`, {
        params: { branchId },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setProjects(res.data?.data || []);
      } else {
        setProjects([]);
        toast.error(res.data?.message || "Failed to load projects");
      }
    } catch (err) {
      console.error("fetchProjects error:", err);
      setProjects([]);
      toast.error("Failed to load projects");
    }
  };

  const fetchGrid = async () => {
    setLoadingGrid(true);
    try {
      const res = await axios.get(`${API_BASE}/list`, {
        params: { branchId },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setBillRows(res.data?.data || []);
      } else {
        setBillRows([]);
        toast.error(res.data?.message || "Failed to load bills");
      }
    } catch (err) {
      console.error("fetchGrid error:", err);
      setBillRows([]);
      toast.error("Failed to load bills");
    } finally {
      setLoadingGrid(false);
    }
  };

  const viewBill = async (id) => {
    try {
      const res = await axios.get(`${API_BASE}/view/${id}`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load bill");
        return;
      }

      const d = res.data?.data || {};

      setMode("U");
      setBillGenerationId(d.billGenerationId || id);
      setLockProjectPick(true);

      setFormData({
        ...emptyForm,
        projectId: String(d.projectId || ""),
        billNo: d.billNo || "",
        brNo: d.brNo || "",
        mbNo: d.mbNo || "",
        billAmount: d.billAmount ?? "",
        it: d.it ?? "",
        itPercent: d.itPercent ?? "",
        st: d.st ?? "",
        stPercent: d.stPercent ?? "",
        fsd: d.fsd ?? "",
        fsdPercent: d.fsdPercent ?? "",
        cwc: d.cwc ?? "",
        cwcPercent: d.cwcPercent ?? "",
        cbf: d.cbf ?? "",
        cbfPercent: d.cbfPercent ?? "",
        penalty: d.penalty ?? "",
        byCheque: d.byCheque ?? "",
        byAdj: d.byAdj ?? "",
        paid: !!d.paid,
        payDate: normalizeDateIn(d.payDate),
        chqNo: d.chqNo || "",
        dateOfRealisation: normalizeDateIn(d.dateOfRealisation),
        refundDate: normalizeDateIn(d.refundDate),
        remarks: d.remarks || "",
        otherRecoveries1: d.otherRecoveries1 ?? "",
        otherRecoveries2: d.otherRecoveries2 ?? "",
      });

      setShowFilters(true);
    } catch (err) {
      console.error("viewBill error:", err);
      toast.error("Failed to load bill");
    }
  };

  const saveBill = async () => {
    if (isLockedByPaid) {
      toast.error("This bill is marked as Paid. Editing is locked.");
      return;
    }

    if (!validate()) return;

    const payload = {
      mode,
      billGenerationId,
      branchId,
      loginUserId,
      projectId: parseInt(formData.projectId || "0", 10),

      billNo: formData.billNo,
      brNo: formData.brNo,
      mbNo: formData.mbNo,
      billAmount: formData.billAmount,

      it: formData.it,
      itPercent: formData.itPercent,
      st: formData.st,
      stPercent: formData.stPercent,
      fsd: formData.fsd,
      fsdPercent: formData.fsdPercent,
      cwc: formData.cwc,
      cwcPercent: formData.cwcPercent,
      cbf: formData.cbf,
      cbfPercent: formData.cbfPercent,

      penalty: formData.penalty,
      byCheque: formData.byCheque,
      byAdj: formData.byAdj,

      paid: !!formData.paid,
      payDate: dateForSave(formData.payDate),
      chqNo: formData.chqNo,
      dateOfRealisation: dateForSave(formData.dateOfRealisation),
      refundDate: dateForSave(formData.refundDate),

      remarks: formData.remarks,
      otherRecoveries1: formData.otherRecoveries1,
      otherRecoveries2: formData.otherRecoveries2,
    };

    try {
      const res = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Saved Successfully");

        const newId = res.data?.data?.billGenerationId;
        if (newId) {
          setMode("U");
          setBillGenerationId(newId);
          setLockProjectPick(true);
        }

        await fetchGrid();
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error("saveBill error:", err);
      toast.error(err?.response?.data?.message || "Save failed");
    }
  };

  useEffect(() => {
    if (!branchId || !loginUserId) {
      toast.error("Missing branchId/loginUserId in localStorage");
    }

    if (branchId) {
      fetchProjects();
      fetchGrid();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, loginUserId]);

  const projectOptions = projects || [];

  return (
    <ProjectPostOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BILL GENERATION</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />
        
        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Bill Generation {mode === "U" ? "(Edit)" : "(New)"}
            </h1>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Form" : "Show Form"}
              </button>

              <button
                onClick={resetToNew}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaUndo />
                New
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Project Name <span className="text-red-500">*</span>
                  </label>

                  <select
                    value={formData.projectId}
                    disabled={lockProjectPick}
                    onChange={(e) => setField("projectId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    } ${lockProjectPick ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <option value="">Select</option>
                    {projectOptions.map((p) => (
                      <option key={p.projectId} value={p.projectId}>
                        {p.projectName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Project Code <span className="text-red-500">*</span>
                  </label>

                  <select
                    value={formData.projectId}
                    disabled={lockProjectPick}
                    onChange={(e) => setField("projectId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    } ${lockProjectPick ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <option value="">Select</option>
                    {projectOptions.map((p) => (
                      <option key={p.projectId} value={p.projectId}>
                        {p.projectCode}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Bill No <span className="text-red-500">*</span>
                  </label>
                  <input
                    disabled={isLockedByPaid}
                    type="text"
                    value={formData.billNo}
                    onChange={(e) => setField("billNo", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    } ${isLockedByPaid ? "opacity-60 cursor-not-allowed" : ""}`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    B.R No
                  </label>
                  <input
                    disabled={isLockedByPaid}
                    type="text"
                    value={formData.brNo}
                    onChange={(e) => setField("brNo", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    } ${isLockedByPaid ? "opacity-60 cursor-not-allowed" : ""}`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    M.B No <span className="text-red-500">*</span>
                  </label>
                  <input
                    disabled={isLockedByPaid}
                    type="text"
                    value={formData.mbNo}
                    onChange={(e) => setField("mbNo", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    } ${isLockedByPaid ? "opacity-60 cursor-not-allowed" : ""}`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Bill Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    disabled={isLockedByPaid}
                    type="text"
                    value={formData.billAmount}
                    onChange={(e) => setField("billAmount", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    } ${isLockedByPaid ? "opacity-60 cursor-not-allowed" : ""}`}
                  />
                </div>

                <TaxRow
                  label="IT"
                  isDark={isDark}
                  locked={isLockedByPaid}
                  value={formData.it}
                  percent={formData.itPercent}
                  onValue={(v) => setField("it", v)}
                  onPercent={(v) => setField("itPercent", v)}
                />

                <TaxRow
                  label="ST"
                  isDark={isDark}
                  locked={isLockedByPaid}
                  value={formData.st}
                  percent={formData.stPercent}
                  onValue={(v) => setField("st", v)}
                  onPercent={(v) => setField("stPercent", v)}
                />

                <TaxRow
                  label="F.S.D"
                  isDark={isDark}
                  locked={isLockedByPaid}
                  value={formData.fsd}
                  percent={formData.fsdPercent}
                  onValue={(v) => setField("fsd", v)}
                  onPercent={(v) => setField("fsdPercent", v)}
                />

                <TaxRow
                  label="C.W.C"
                  isDark={isDark}
                  locked={isLockedByPaid}
                  value={formData.cwc}
                  percent={formData.cwcPercent}
                  onValue={(v) => setField("cwc", v)}
                  onPercent={(v) => setField("cwcPercent", v)}
                />

                <TaxRow
                  label="C.B.F"
                  isDark={isDark}
                  locked={isLockedByPaid}
                  value={formData.cbf}
                  percent={formData.cbfPercent}
                  onValue={(v) => setField("cbf", v)}
                  onPercent={(v) => setField("cbfPercent", v)}
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="paid"
                    checked={!!formData.paid}
                    onChange={(e) => {
                      if (mode === "U" && !!formData.paid) return;
                      setField("paid", e.target.checked);
                    }}
                    disabled={mode === "U" && !!formData.paid}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="paid" className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Paid (Locks editing once saved as Paid)
                  </label>
                </div>

                <Input
                  isDark={isDark}
                  locked={isLockedByPaid}
                  label="By Adj"
                  value={formData.byAdj}
                  onChange={(v) => setField("byAdj", v)}
                />

                <Input
                  isDark={isDark}
                  locked={isLockedByPaid}
                  label="By Cheque"
                  value={formData.byCheque}
                  onChange={(v) => setField("byCheque", v)}
                />

                <Input
                  isDark={isDark}
                  locked={isLockedByPaid}
                  label="Penalty"
                  value={formData.penalty}
                  onChange={(v) => setField("penalty", v)}
                />

                <DateInput
                  isDark={isDark}
                  locked={isLockedByPaid}
                  label="Pay Date"
                  required
                  value={formData.payDate}
                  onChange={(v) => setField("payDate", v)}
                />

                <Input
                  isDark={isDark}
                  locked={isLockedByPaid}
                  label="Chq No"
                  value={formData.chqNo}
                  onChange={(v) => setField("chqNo", v)}
                />

                <DateInput
                  isDark={isDark}
                  locked={isLockedByPaid}
                  label="Date Of Realisation"
                  required
                  value={formData.dateOfRealisation}
                  onChange={(v) => setField("dateOfRealisation", v)}
                />

                <DateInput
                  isDark={isDark}
                  locked={isLockedByPaid}
                  label="Refund Date"
                  value={formData.refundDate}
                  onChange={(v) => setField("refundDate", v)}
                />

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Remarks
                  </label>
                  <textarea
                    disabled={isLockedByPaid}
                    value={formData.remarks}
                    onChange={(e) => setField("remarks", e.target.value)}
                    rows={3}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    } ${isLockedByPaid ? "opacity-60 cursor-not-allowed" : ""}`}
                  />
                </div>

                <Input
                  isDark={isDark}
                  locked={isLockedByPaid}
                  label="Other Recoveries 1"
                  value={formData.otherRecoveries1}
                  onChange={(v) => setField("otherRecoveries1", v)}
                />

                <Input
                  isDark={isDark}
                  locked={isLockedByPaid}
                  label="Other Recoveries 2"
                  value={formData.otherRecoveries2}
                  onChange={(v) => setField("otherRecoveries2", v)}
                />
              </div>
            </div>
          )}

          <div className="flex justify-center items-center gap-6 mb-8">
            <button
              onClick={resetToNew}
              className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
              }`}
            >
              <FaPlus />
              Add New
            </button>

            <button
              onClick={saveBill}
              className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isLockedByPaid
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : isDark
                  ? "bg-gray-600 hover:bg-gray-700 text-white"
                  : "bg-gray-500 hover:bg-gray-600 text-white"
              }`}
              disabled={isLockedByPaid}
            >
              <FaSave />
              Save
            </button>
          </div>

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="flex items-center justify-between px-4 py-3">
              <div className={`${isDark ? "text-gray-200" : "text-gray-700"} font-semibold`}>
                Bills List {loadingGrid ? "(Loading...)" : `(${billRows.length})`}
              </div>
              <button
                onClick={fetchGrid}
                className={`px-3 py-2 rounded-lg text-sm font-semibold ${
                  isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                }`}
              >
                Refresh
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">#</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Bill No</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Project Code</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Project Name</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Bill Amount</th>
                    <th className="px-4 py-4 text-center font-semibold">View</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {billRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={`px-4 py-8 text-center ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                        No records
                      </td>
                    </tr>
                  ) : (
                    billRows.map((r, idx) => (
                      <tr
                        key={r.billGenerationId || idx}
                        className={`transition-colors duration-200 ${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"}`}
                      >
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {idx + 1}
                        </td>

                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.billNo}
                        </td>

                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.projectCode}
                        </td>

                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.projectName}
                        </td>

                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {fmtMoney0(r.billAmount)}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => viewBill(r.billGenerationId)}
                            className={`px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${
                              isDark ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-100 hover:bg-blue-200 text-blue-800"
                            }`}
                            title="View / Edit"
                          >
                            <FaEye />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPostOrderLayoutWrapper>
  );
};

export default BillGeneration;

function Input({ label, value, onChange, isDark, locked }) {
  return (
    <div>
      <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
        {label}
      </label>
      <input
        disabled={locked}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
        } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
      />
    </div>
  );
}

// ✅ UPDATED: now a native calendar date picker (type="date").
// We still store/save the value as dd/MM/yyyy everywhere else in the file,
// so this component converts to/from ISO (yyyy-MM-dd) only at the edges:
// - displays dmyToISO(value) in the picker
// - converts the picker's ISO output back to dd/MM/yyyy via isoToDDMMYYYY
//   before calling onChange, so formData/save payload/validate logic is unchanged.
// - the required "*" is rendered in red via a separate span.
function DateInput({ label, value, onChange, isDark, locked, required }) {
  const isoValue = dmyToISO(value);

  return (
    <div>
      <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      <input
        disabled={locked}
        type="date"
        value={isoValue}
        onChange={(e) => onChange(isoToDDMMYYYY(e.target.value))}
        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
          isDark ? "bg-gray-700 border-gray-600 text-white placeholder-gray-300" : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
        } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
      />
    </div>
  );
}

function TaxRow({ label, value, percent, onValue, onPercent, isDark, locked }) {
  return (
    <div>
      <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
        {label}
      </label>
      <div className="flex gap-2">
        <input
          disabled={locked}
          type="text"
          value={value}
          onChange={(e) => onValue(e.target.value)}
          className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
          } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
        />
        <input
          disabled={locked}
          type="text"
          value={percent}
          onChange={(e) => onPercent(e.target.value)}
          placeholder="%"
          className={`w-20 px-2 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
            isDark ? "bg-gray-700 border-gray-600 text-white placeholder-gray-300" : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
          } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
        />
      </div>
    </div>
  );
}
